import { mix, pack, parseCssColor, u32 } from "../shared/color";
import type { BlockRec, BoxPaint, ExtractOpts, Glyph, Grad, ImgPaint, Level, PageLink, Paint, RawImage, Rect, Shadow } from "../shared/types";

const SKIP = new Set(["script", "style", "head", "meta", "link", "title", "template", "noscript", "br", "wbr", "option", "optgroup", "datalist", "map", "area", "track", "source", "param"]);
const MEDIA = new Set(["img", "svg", "video", "canvas", "iframe", "embed", "object"]);
const CONTROLS = new Set(["input", "textarea", "select", "button"]);

const yieldNow = () => new Promise((r) => setTimeout(r, 0));

let probe: CanvasRenderingContext2D | null = null;
const named = new Map<string, number | null>();

function readColor(input: string | null | undefined): number | null {
  const direct = parseCssColor(input);
  if (direct !== null || !input) return direct;
  const s = input.trim();
  if (/^(rgba?\(|#|transparent)/i.test(s)) return direct;
  if (named.has(s)) return named.get(s) ?? null;
  if (!probe) {
    const c = document.createElement("canvas");
    c.width = c.height = 1;
    probe = c.getContext("2d", { willReadFrequently: true });
  }
  if (!probe) return null;
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = "rgba(0,0,0,0)";
  probe.fillStyle = s;
  probe.fillRect(0, 0, 1, 1);
  const d = probe.getImageData(0, 0, 1, 1).data;
  const color = d[3] < 3 ? null : pack(Math.round((d[0] * 255) / d[3]), Math.round((d[1] * 255) / d[3]), Math.round((d[2] * 255) / d[3]), d[3]);
  named.set(s, color);
  return color;
}

let proxyTok = "";
/** Fetch token for /p/ URLs built here; the page frame cannot send the cookie. */
export function setProxyToken(tok: string) {
  proxyTok = tok;
}

export function proxyUrl(raw: string | null | undefined, origin: string, base: string): string | null {
  if (!raw) return null;
  if (raw.startsWith("data:")) return raw;
  if (raw.startsWith("blob:")) return null;
  if (raw.startsWith(origin)) return raw;
  try {
    const abs = new URL(raw, base);
    if (abs.protocol !== "http:" && abs.protocol !== "https:") return null;
    return `${origin}/p/${proxyTok ? `${proxyTok}/` : ""}${abs.protocol.slice(0, -1)}/${abs.host}${abs.pathname}${abs.search}`;
  } catch {
    return null;
  }
}

function over(fg: number, bg: number, opacity = 1): number {
  const a = ((u32(fg) >>> 24) / 255) * opacity;
  return mix(u32(bg) | 0xff000000, u32(fg) | 0xff000000, a);
}

function dist(a: number, b: number): number {
  a = u32(a);
  b = u32(b);
  const dr = (a & 255) - (b & 255);
  const dg = ((a >>> 8) & 255) - ((b >>> 8) & 255);
  const db = ((a >>> 16) & 255) - ((b >>> 16) & 255);
  return Math.hypot(dr, dg, db);
}

function splitTop(value: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (const ch of value) {
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

function parseStops(body: string): { color: number; pos: number }[] {
  const parts = splitTop(body);
  const stops: { color: number; pos: number }[] = [];
  parts.forEach((part, i) => {
    const bits = part.trim().split(/\s+/);
    let pos: number | null = null;
    const colorBits: string[] = [];
    for (const b of bits) {
      if (b.endsWith("%") && colorBits.length) pos = parseFloat(b) / 100;
      else colorBits.push(b);
    }
    const color = readColor(colorBits.join(" "));
    if (color === null) return;
    stops.push({ color, pos: pos ?? i / Math.max(1, parts.length - 1) });
  });
  if (stops.length >= 2) {
    if (stops[0].pos === 0 && stops.length > 1) {
      /* keep */
    }
    stops[0].pos = stops[0].pos || 0;
    stops[stops.length - 1].pos = stops[stops.length - 1].pos || 1;
  }
  return stops;
}

function angleOf(token: string): number | null {
  const t = token.trim().toLowerCase();
  // CSS 0deg points up, which is angle 0 for the renderer (sin 0, -cos 0).
  if (t.endsWith("deg")) return (parseFloat(t) * Math.PI) / 180;
  if (t.endsWith("turn")) return parseFloat(t) * Math.PI * 2;
  if (t.endsWith("rad")) return parseFloat(t);
  const map: Record<string, number> = {
    "to top": 0,
    "to bottom": Math.PI,
    "to right": Math.PI / 2,
    "to left": -Math.PI / 2,
    "to top right": Math.PI / 4,
    "to right top": Math.PI / 4,
    "to bottom right": (Math.PI * 3) / 4,
    "to right bottom": (Math.PI * 3) / 4,
    "to bottom left": (-Math.PI * 3) / 4,
    "to left bottom": (-Math.PI * 3) / 4,
    "to top left": -Math.PI / 4,
    "to left top": -Math.PI / 4,
  };
  return map[t] ?? null;
}

function gradients(bg: string): Grad[] {
  if (!bg || bg === "none") return [];
  const grads: Grad[] = [];
  for (const layer of splitTop(bg)) {
    const m = layer.match(/^(linear|radial)-gradient\((.*)\)$/i);
    if (!m) continue;
    const type = m[1].toLowerCase() as "linear" | "radial";
    const inner = splitTop(m[2]);
    let angle = type === "linear" ? Math.PI : 0;
    let rest = inner;
    const ang = angleOf(inner[0] ?? "");
    if (ang !== null && !readColor(inner[0])) {
      angle = ang;
      rest = inner.slice(1);
    } else if (/deg|turn|rad/.test(inner[0] ?? "")) {
      rest = inner.slice(1);
    }
    const stops = parseStops(rest.join(", "));
    if (stops.length) grads.push({ type, angle, stops });
  }
  return grads;
}

function radii(style: CSSStyleDeclaration, box: Rect): number[] {
  const one = (raw: string) => {
    const bit = (raw || "0").split(" ")[0];
    const n = parseFloat(bit) || 0;
    return bit.endsWith("%") ? (n / 100) * Math.min(box.w, box.h) : n;
  };
  return [one(style.borderTopLeftRadius), one(style.borderTopRightRadius), one(style.borderBottomRightRadius), one(style.borderBottomLeftRadius)];
}

function borderOf(style: CSSStyleDeclaration): { w: number[]; c: number[] } | null {
  const side = (width: string, kind: string, color: string): [number, number] => {
    if (kind === "none" || kind === "hidden") return [0, 0];
    const w = parseFloat(width) || 0;
    const c = readColor(color);
    if (!c || w < 0.5 || (u32(c) >>> 24) < 20) return [0, 0];
    return [w, c];
  };
  const t = side(style.borderTopWidth, style.borderTopStyle, style.borderTopColor);
  const r = side(style.borderRightWidth, style.borderRightStyle, style.borderRightColor);
  const b = side(style.borderBottomWidth, style.borderBottomStyle, style.borderBottomColor);
  const l = side(style.borderLeftWidth, style.borderLeftStyle, style.borderLeftColor);
  if (t[0] + r[0] + b[0] + l[0] < 0.5) return null;
  return { w: [t[0], r[0], b[0], l[0]], c: [t[1], r[1], b[1], l[1]] };
}

function shadowsOf(raw: string): Shadow[] {
  if (!raw || raw === "none") return [];
  const out: Shadow[] = [];
  for (const part of splitTop(raw)) {
    if (/inset/.test(part)) continue;
    const colorMatch = part.match(/rgba?\([^)]+\)|#[0-9a-f]{3,8}|[a-z]+$/i);
    const color = readColor(colorMatch?.[0] ?? "") ?? pack(0, 0, 0, 40);
    const nums = part.replace(/rgba?\([^)]+\)|#[0-9a-f]{3,8}/gi, "").match(/-?[\d.]+px/g)?.map((n) => parseFloat(n)) ?? [];
    if (nums.length < 2) continue;
    out.push({ x: nums[0], y: nums[1], blur: nums[2] ?? 0, spread: nums[3] ?? 0, color });
  }
  return out.slice(0, 3);
}

function isLogo(el: Element): boolean {
  const bits = [el.id, el.getAttribute("class") || "", el.getAttribute("alt") || "", el.getAttribute("aria-label") || "", el.getAttribute("title") || "", el.tagName].join(" ");
  if (/\blogo\b/i.test(bits)) return true;
  const parent = el.parentElement;
  if (!parent) return false;
  const up = [parent.id, parent.getAttribute("class") || "", parent.getAttribute("aria-label") || "", parent.getAttribute("title") || "", parent.tagName].join(" ");
  return /\blogo\b/i.test(up);
}

function clipIntersect(a: Rect | null, b: Rect): Rect {
  if (!a) return { ...b };
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const r = Math.min(a.x + a.w, b.x + b.w);
  const bo = Math.min(a.y + a.h, b.y + b.h);
  return { x, y, w: Math.max(0, r - x), h: Math.max(0, bo - y) };
}

function fontShorthand(style: CSSStyleDeclaration, size: number): string {
  const italic = style.fontStyle && style.fontStyle !== "normal" ? "italic " : "";
  const weight = /^\d+$/.test(style.fontWeight) ? style.fontWeight : style.fontWeight === "bold" ? "700" : "400";
  return `${italic}${weight} ${size}px ${style.fontFamily || "sans-serif"}`;
}

function pixelated(style: CSSStyleDeclaration): boolean {
  return /pixelated|crisp-edges|optimize-contrast|optimizespeed/i.test(style.imageRendering || "");
}

function objectFitBox(style: CSSStyleDeclaration, box: Rect, nw: number, nh: number) {
  const pl = parseFloat(style.paddingLeft) + parseFloat(style.borderLeftWidth);
  const pt = parseFloat(style.paddingTop) + parseFloat(style.borderTopWidth);
  const pr = parseFloat(style.paddingRight) + parseFloat(style.borderRightWidth);
  const pb = parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth);
  const x = box.x + pl;
  const y = box.y + pt;
  const w = Math.max(1, box.w - pl - pr);
  const h = Math.max(1, box.h - pt - pb);
  const fit = style.objectFit || "fill";
  let dw = w;
  let dh = h;
  if (fit === "contain" || fit === "cover" || fit === "scale-down" || fit === "none") {
    let m = fit === "cover" ? Math.max(w / nw, h / nh) : fit === "none" ? 1 : Math.min(w / nw, h / nh);
    if (fit === "scale-down") m = Math.min(1, m);
    dw = nw * m;
    dh = nh * m;
  }
  const [px = "50%", py = "50%"] = (style.objectPosition || "50% 50%").split(/\s+/);
  const axis = (token: string, space: number) => (token.endsWith("%") ? (parseFloat(token) / 100) * space : parseFloat(token) || 0);
  return { x, y, w, h, dx: x + axis(px, w - dw), dy: y + axis(py, h - dh), dw, dh };
}

function bgImages(style: CSSStyleDeclaration, box: Rect, resolve: (u: string) => string | null): RawImage[] {
  const bg = style.backgroundImage;
  if (!bg || bg === "none" || !bg.includes("url(")) return [];
  const layers = splitTop(bg);
  const sizes = splitTop(style.backgroundSize || "auto");
  const positions = splitTop(style.backgroundPosition || "0% 0%");
  const repeats = splitTop(style.backgroundRepeat || "repeat");
  const out: RawImage[] = [];
  layers.forEach((layer, i) => {
    const m = layer.match(/^url\(\s*["']?([^"')]+)["']?\s*\)$/);
    if (!m) return;
    const url = resolve(m[1]);
    if (!url) return;
    out.push({
      url,
      size: sizes[i % sizes.length] ?? "auto",
      pos: (positions[i % positions.length] ?? "0% 0%").split(/\s+/),
      rep: repeats[i % repeats.length] ?? "repeat",
      rect: { ...box },
      pixelated: pixelated(style) || undefined,
    });
  });
  return out;
}

function svgData(el: Element, view: Window, box: Rect): string | null {
  try {
    const clone = el.cloneNode(true) as SVGElement;
    const srcNodes = [el, ...Array.from(el.querySelectorAll("*"))];
    const dstNodes = [clone, ...Array.from(clone.querySelectorAll("*"))];
    const props = ["fill", "stroke", "stroke-width", "opacity", "fill-opacity", "stroke-opacity", "fill-rule", "stroke-linecap", "stroke-linejoin", "stop-color", "stop-opacity", "font-family", "font-size", "font-weight", "text-anchor", "display", "visibility"];
    for (let i = 0; i < srcNodes.length && i < dstNodes.length; i++) {
      const cs = view.getComputedStyle(srcNodes[i]);
      const bits: string[] = [];
      for (const p of props) {
        const v = cs.getPropertyValue(p);
        if (v) bits.push(`${p}:${v}`);
      }
      dstNodes[i].setAttribute("style", bits.join(";"));
    }
    const defs = el.ownerDocument.createElementNS("http://www.w3.org/2000/svg", "defs");
    clone.querySelectorAll("use").forEach((use) => {
      try {
        const href = use.getAttribute("href") || use.getAttribute("xlink:href") || "";
        if (!href.startsWith("#") || clone.querySelector(href)) return;
        const target = el.ownerDocument.querySelector(href);
        if (target) defs.appendChild(target.cloneNode(true));
      } catch {
        /* a broken href should not drop the rest of the icon */
      }
    });
    if (defs.childNodes.length) clone.insertBefore(defs, clone.firstChild);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    clone.setAttribute("width", String(box.w));
    clone.setAttribute("height", String(box.h));
    clone.style.color = view.getComputedStyle(el).color;
    const xml = new XMLSerializer().serializeToString(clone);
    if (xml.length > 400000) return null;
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
  } catch {
    return null;
  }
}

function fontFaces(doc: Document, origin: string): string[] {
  const out: string[] = [];
  const walk = (sheet: CSSStyleSheet, depth: number) => {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      return;
    }
    const base = sheet.href || doc.baseURI;
    const scan = (list: CSSRuleList) => {
      for (let i = 0; i < list.length && out.length < 300; i++) {
        const rule = list[i];
        if (rule.type === CSSRule.FONT_FACE_RULE) {
          out.push(rule.cssText.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (_m, _q, url) => {
            let abs = url;
            try {
              abs = new URL(url, base).href;
            } catch {
              /* keep */
            }
            return `url("${proxyUrl(abs, origin, base) ?? abs}")`;
          }));
        } else if (rule.type === CSSRule.IMPORT_RULE && depth < 4) {
          const imported = (rule as CSSImportRule).styleSheet;
          if (imported) walk(imported, depth + 1);
        } else if ((rule.type === CSSRule.MEDIA_RULE || rule.type === CSSRule.SUPPORTS_RULE) && (rule as CSSGroupingRule).cssRules) {
          scan((rule as CSSGroupingRule).cssRules);
        }
      }
    };
    scan(rules);
  };
  for (const sheet of Array.from(doc.styleSheets)) walk(sheet, 0);
  return out;
}

interface Frame {
  op: number;
  clip: Rect | null;
  z: number;
  block: number;
  under: number;
}

/**
 * Walk a document the same way the original in-page extractor does:
 * paints, per-glyph rects, and destructible blocks.
 */
export async function extractLevel(doc: Document, view: Window, opts: ExtractOpts, progress: (msg: string, p: number) => void): Promise<Level> {
  const title = doc.title || new URL(opts.pageUrl).hostname;
  const scrollH = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0);
  const width = opts.layoutW;
  const height = Math.round(Math.max(opts.viewH, Math.min(opts.maxH, scrollH)));
  const baseHref = doc.baseURI || opts.pageUrl;
  const resolve = (u: string) => proxyUrl(u, opts.origin, baseHref);
  const measure = doc.createElement("canvas").getContext("2d");
  if (!measure) throw new Error("Canvas is not available.");
  const metrics = new Map<string, { asc: number; desc: number; xh: number }>();
  const metric = (font: string, size: number) => {
    let hit = metrics.get(font);
    if (!hit) {
      measure.font = font;
      const mg = measure.measureText("Mg");
      const x = measure.measureText("x");
      hit = {
        asc: mg.fontBoundingBoxAscent || size * 0.8,
        desc: mg.fontBoundingBoxDescent || size * 0.2,
        xh: x.actualBoundingBoxAscent || size * 0.5,
      };
      metrics.set(font, hit);
    }
    return hit;
  };

  const paints: { p: Paint; z: number; n: number }[] = [];
  const glyphs: Glyph[] = [];
  const blocks: BlockRec[] = [];
  const links: PageLink[] = [];
  const fonts = new Set<string>();
  const linkBase = anchorBase(doc, opts.pageUrl, opts.origin);
  let order = 0;
  let seen = 0;

  const rootStyle = view.getComputedStyle(doc.documentElement);
  const bodyStyle = doc.body ? view.getComputedStyle(doc.body) : rootStyle;
  const rootBg = readColor(rootStyle.backgroundColor);
  const bodyBg = readColor(bodyStyle.backgroundColor);
  const pageBg = rootBg ?? bodyBg ?? pack(255, 255, 255);
  let under = over(pageBg, pack(255, 255, 255));
  const base = under;

  if (doc.body && rootBg !== null && bodyBg !== null && bodyBg !== rootBg) {
    const r = doc.body.getBoundingClientRect();
    paints.push({
      p: box(r.left, r.top, r.width, Math.max(r.height, height - r.top), bodyBg, [], [], null, [], 1, null, [0, 0, 0, 0]),
      z: -1e9,
      n: order++,
    });
    under = over(bodyBg, base);
  }
  for (const style of [rootStyle, bodyStyle]) {
    if (style.backgroundImage && style.backgroundImage !== "none") {
      const grads = gradients(style.backgroundImage);
      if (grads.length) {
        paints.push({
          p: box(0, 0, width, height, null, grads, [], null, [], 1, null, [0, 0, 0, 0]),
          z: -1e9,
          n: order++,
        });
      }
    }
  }

  const range = doc.createRange();

  const pushGlyph = (g: Glyph) => {
    if (glyphs.length < opts.maxGlyphs) glyphs.push(g);
  };

  const textNode = (node: Text, style: CSSStyleDeclaration, opacity: number, clip: Rect | null) => {
    const data = node.data;
    if (!data.trim() || glyphs.length >= opts.maxGlyphs) return;
    const size = parseFloat(style.fontSize) || 16;
    if (size < 3) return;
    const webkit = style as CSSStyleDeclaration & { webkitTextFillColor?: string; webkitBackgroundClip?: string };
    let color = readColor(webkit.webkitTextFillColor) ?? readColor(style.color);
    const clipText = (style.backgroundClip || webkit.webkitBackgroundClip || "").includes("text");
    if (clipText) {
      const g = gradients(style.backgroundImage)[0];
      if (g) color = g.stops[Math.floor(g.stops.length / 2)]?.color ?? color;
      else color = color ?? readColor(style.backgroundColor);
    }
    if (color === null) return;
    const a = Math.round(((u32(color) >>> 24) / 255) * opacity * 255);
    if (a < 10) return;
    color = (a << 24) | (u32(color) & 0xffffff);
    const font = fontShorthand(style, size);
    fonts.add(font);
    const met = metric(font, size);
    const transform = style.textTransform;
    const underline = (style.textDecorationLine || "").includes("underline");
    let offset = 0;
    let word = true;
    for (const ch of data) {
      const len = ch.length;
      if (/\s/.test(ch)) {
        offset += len;
        word = true;
        continue;
      }
      range.setStart(node, offset);
      range.setEnd(node, offset + len);
      offset += len;
      let shown = ch;
      if (transform === "uppercase") shown = shown.toUpperCase();
      else if (transform === "lowercase") shown = shown.toLowerCase();
      else if (transform === "capitalize" && word) shown = shown.toUpperCase();
      word = false;
      const rect = range.getClientRects()[0];
      if (!rect || rect.width < 0.3 || rect.height < 1) continue;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      if (cx < 0 || cx > width || cy < 0 || cy > height) continue;
      if (clip && (cx < clip.x || cx > clip.x + clip.w || cy < clip.y || cy > clip.y + clip.h)) continue;
      const baseline = rect.top + (rect.height - (met.asc + met.desc)) / 2 + met.asc;
      pushGlyph({
        ch: shown,
        x: rect.left,
        y: rect.top,
        w: rect.width,
        h: rect.height,
        base: baseline,
        font,
        size,
        xh: met.xh,
        color,
        underline,
      });
    }
  };

  const controlText = (el: Element, style: CSSStyleDeclaration, box: Rect, opacity: number, clip: Rect | null) => {
    const input = el as HTMLInputElement;
    const kind = (input.type || "").toLowerCase();
    if (["checkbox", "radio", "range", "color", "file", "hidden", "image"].includes(kind)) return;
    let value = "";
    let color = readColor(style.color);
    const tag = el.tagName.toLowerCase();
    if (tag === "select") value = (el as HTMLSelectElement).selectedOptions?.[0]?.text ?? "";
    else if (input.value) value = kind === "password" ? "•".repeat(input.value.length) : input.value;
    else if (input.placeholder) {
      value = input.placeholder;
      color = readColor(view.getComputedStyle(el, "::placeholder").color) ?? color;
    }
    value = value.split("\n")[0].slice(0, 120);
    if (!value || color === null) return;
    const size = parseFloat(style.fontSize) || 14;
    const font = fontShorthand(style, size);
    fonts.add(font);
    const met = metric(font, size);
    measure.font = font;
    const pad = parseFloat(style.paddingLeft) + parseFloat(style.borderLeftWidth);
    const textW = measure.measureText(value).width;
    let x = box.x + pad;
    if (style.textAlign === "center" || kind === "submit" || kind === "button" || kind === "reset") x = box.x + (box.w - textW) / 2;
    const h = met.asc + met.desc;
    const y = tag === "textarea" ? box.y + parseFloat(style.paddingTop) + parseFloat(style.borderTopWidth) : box.y + (box.h - h) / 2;
    const painted = (Math.round(((u32(color) >>> 24) / 255) * opacity * 255) << 24) | (u32(color) & 0xffffff);
    for (const ch of value) {
      const cw = measure.measureText(ch).width;
      if (ch.trim() && x + cw <= box.x + box.w - 2 && glyphs.length < opts.maxGlyphs) {
        if (!clip || (x > clip.x && x < clip.x + clip.w)) {
          pushGlyph({ ch, x, y, w: cw, h, base: y + met.asc, font, size, xh: met.xh, color: painted, underline: false });
        }
      }
      x += cw;
    }
  };

  const walk = async (el: Element, frame: Frame): Promise<void> => {
    if (glyphs.length >= opts.maxGlyphs && paints.length > 8000) return;
    const tag = el.tagName.toLowerCase();
    if (SKIP.has(tag)) return;
    const style = view.getComputedStyle(el);
    if (style.display === "none") return;
    if (++seen % 300 === 0) {
      progress("Reading the page", 0.5 + Math.min(0.3, seen / 30000));
      await yieldNow();
    }
    const opacity = frame.op * (parseFloat(style.opacity) || (style.opacity === "0" ? 0 : 1));
    if (opacity < 0.02) return;
    const visible = style.visibility !== "hidden" && style.visibility !== "collapse";
    const rect = el.getBoundingClientRect();
    const boxR: Rect = { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
    let z = frame.z;
    if (style.position !== "static") z = style.zIndex !== "auto" ? parseInt(style.zIndex) || 0 : frame.z + 0.001;
    const onScreen = boxR.x < width && boxR.x + boxR.w > 0 && boxR.y < height && boxR.y + boxR.h > 0;
    const isRoot = el === doc.documentElement || el === doc.body;
    let parentBlock = frame.block;
    let underColor = frame.under;
    let own = -1;

    if (tag === "a" && visible && boxR.w >= 4 && boxR.h >= 4 && boxR.y < height && boxR.y + boxR.h > 0 && boxR.x < width && boxR.x + boxR.w > 0) {
      const href = resolveAnchor(el.getAttribute("href") || "", linkBase, opts.origin);
      if (href && links.length < 800) links.push({ href, x: boxR.x, y: boxR.y, w: boxR.w, h: boxR.h });
    }

    if (visible && onScreen && boxR.w > 0.5 && boxR.h > 0.5 && !isRoot) {
      const radius = radii(style, boxR);
      const bg = readColor(style.backgroundColor);
      const textClip = (style.backgroundClip || (style as CSSStyleDeclaration & { webkitBackgroundClip?: string }).webkitBackgroundClip || "").includes("text");
      const grads = textClip ? [] : gradients(style.backgroundImage);
      const images = textClip ? [] : bgImages(style, boxR, resolve);
      const border = borderOf(style);
      const shadows = shadowsOf(style.boxShadow);
      const painted = (bg !== null && (u32(bg) >>> 24) > 8 && !textClip) || grads.length > 0 || images.length > 0;
      if (painted || border || shadows.length) {
        paints.push({
          p: box(boxR.x, boxR.y, boxR.w, boxR.h, textClip ? null : bg, grads, images, border, painted ? shadows : [], opacity, frame.clip, radius),
          z,
          n: order++,
        });
      }
      let composite = underColor;
      if (bg !== null && !textClip) composite = over(bg, underColor, opacity);
      if (grads.length) {
        const stops = grads[0].stops;
        let r = 0, g = 0, b = 0;
        for (const stop of stops) {
          const c = over(stop.color, composite, opacity);
          r += c & 255;
          g += (c >>> 8) & 255;
          b += (c >>> 16) & 255;
        }
        composite = pack(r / stops.length, g / stops.length, b / stops.length);
      }
      let contrastBorder = false;
      if (border) {
        for (let i = 0; i < 4; i++) if (border.w[i] >= 1 && dist(over(border.c[i], underColor, opacity), underColor) > 22) contrastBorder = true;
      }
      for (const sh of shadows) {
        if ((u32(sh.color) >>> 24) / 255 >= 0.06 && (sh.blur >= 2 || Math.abs(sh.y) >= 1)) contrastBorder = true;
      }
      const distinct = images.length > 0 || dist(composite, underColor) > 10 || contrastBorder;

      if (tag === "img") {
        const img = el as HTMLImageElement;
        const src = img.complete && img.naturalWidth > 0 ? resolve(img.currentSrc || img.src) : null;
        if (src) {
          const fit = objectFitBox(style, boxR, img.naturalWidth, img.naturalHeight);
          const paint: ImgPaint = {
            k: "image", x: fit.x, y: fit.y, w: fit.w, h: fit.h, src, dx: fit.dx, dy: fit.dy, dw: fit.dw, dh: fit.dh,
            radius, opacity, clip: frame.clip, pixelated: pixelated(style) || undefined,
          };
          paints.push({ p: paint, z, n: order++ });
        }
      } else if (tag === "svg") {
        const src = svgData(el, view, boxR);
        if (src) {
          paints.push({
            p: { k: "image", x: boxR.x, y: boxR.y, w: boxR.w, h: boxR.h, src, dx: boxR.x, dy: boxR.y, dw: boxR.w, dh: boxR.h, radius: [0, 0, 0, 0], opacity, clip: frame.clip },
            z, n: order++,
          });
        }
      } else if (tag === "canvas") {
        let src: string | null = null;
        try {
          const canvas = el as HTMLCanvasElement;
          if (canvas.width > 0 && canvas.height > 0) src = canvas.toDataURL();
        } catch {
          src = null;
        }
        if (src && src.length > 200) {
          paints.push({
            p: { k: "image", x: boxR.x, y: boxR.y, w: boxR.w, h: boxR.h, src, dx: boxR.x, dy: boxR.y, dw: boxR.w, dh: boxR.h, radius, opacity, clip: frame.clip },
            z, n: order++,
          });
        }
      } else if (tag === "video") {
        const poster = (el as HTMLVideoElement).poster;
        const src = poster ? resolve(poster) : null;
        if (src) {
          paints.push({
            p: { k: "image", x: boxR.x, y: boxR.y, w: boxR.w, h: boxR.h, src, dx: boxR.x, dy: boxR.y, dw: boxR.w, dh: boxR.h, radius, opacity, clip: frame.clip },
            z, n: order++,
          });
        } else {
          paints.push({ p: box(boxR.x, boxR.y, boxR.w, boxR.h, pack(20, 20, 26), [], [], null, [], opacity, frame.clip, radius), z, n: order++ });
        }
      } else if (tag === "iframe" || tag === "embed" || tag === "object") {
        paints.push({ p: box(boxR.x, boxR.y, boxR.w, boxR.h, pack(226, 228, 234), [], [], null, [], opacity, frame.clip, radius), z, n: order++ });
      }

      if (tag === "input" || tag === "textarea" || tag === "select") controlText(el, style, boxR, opacity, frame.clip);

      let kind: BlockRec["kind"] | null = null;
      if (MEDIA.has(tag)) kind = "image";
      else if (CONTROLS.has(tag) && (distinct || tag !== "button")) kind = "control";
      else if (distinct) kind = "box";
      let clipped = boxR;
      if (frame.clip) clipped = clipIntersect(frame.clip, boxR);
      if (kind && clipped.w >= 6 && clipped.h >= 6 && blocks.length < 3000) {
        const area = clipped.w * clipped.h;
        blocks.push({
          x: clipped.x, y: clipped.y, w: clipped.w, h: clipped.h, kind,
          parent: frame.block, radius: radius[0], post: blocks.length,
          canDetach: area < 0.28 * width * opts.viewH && clipped.h < 800,
          mark: kind === "image" && isLogo(el) ? "logo" : undefined,
        });
        parentBlock = own = blocks.length - 1;
      }
      if (painted) underColor = composite;
    }

    if (tag === "svg" || tag === "img" || tag === "video" || tag === "canvas" || tag === "iframe" || tag === "select" || tag === "textarea") {
      if (own >= 0) blocks[own].post = blocks.length - 1;
      return;
    }
    let clip = frame.clip;
    if (!isRoot && (style.overflowX !== "visible" || style.overflowY !== "visible") && style.overflowX !== "") clip = clipIntersect(clip, boxR);
    const next: Frame = { op: opacity, clip, z, block: parentBlock, under: isRoot ? base : underColor };
    const visit = async (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (visible) textNode(node as Text, style, opacity, clip);
      } else if (node.nodeType === Node.ELEMENT_NODE) await walk(node as Element, next);
    };
    for (let child = el.firstChild; child; child = child.nextSibling) await visit(child);
    // Lit and other web components keep the visible tree in an open shadow root.
    const shadow = el.shadowRoot;
    if (shadow) for (let child = shadow.firstChild; child; child = child.nextSibling) await visit(child);
    if (own >= 0) blocks[own].post = blocks.length - 1;
  };

  progress("Reading the page", 0.5);
  await walk(doc.documentElement, { op: 1, clip: null, z: 0, block: -1, under });
  paints.sort((a, b) => a.z - b.z || a.n - b.n);
  // Image addresses stay strings. A bitmap taken from the proxied page is not
  // origin-clean, and posting it makes the whole extract fail.
  return {
    url: opts.pageUrl,
    title,
    width,
    height,
    base,
    paints: paints.map((p) => p.p),
    glyphs,
    blocks,
    fontFaces: fontFaces(doc, opts.origin),
    fonts: [...fonts].slice(0, 60),
    links,
  };
}

/** Resolve anchors against the real page, not a proxied `<base>` that points at this origin. */
function anchorBase(doc: Document, pageUrl: string, origin: string): string {
  const raw = doc.querySelector("base[href]")?.getAttribute("href")?.trim() || "";
  try {
    const page = new URL(pageUrl);
    if (!raw) return page.href;
    const abs = new URL(raw, page);
    const unwrapped = unwrapProxy(abs, origin);
    if (unwrapped && unwrapped.origin !== origin) return unwrapped.href;
    if (abs.origin === origin) return page.href;
    if (abs.protocol === "http:" || abs.protocol === "https:") return abs.href;
    return page.href;
  } catch {
    return pageUrl;
  }
}

function resolveAnchor(raw: string, base: string, origin: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.startsWith("#")) return null;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("javascript:") || lower.startsWith("mailto:") || lower.startsWith("tel:") || lower.startsWith("data:") || lower.startsWith("blob:")) return null;
  try {
    const abs = new URL(trimmed, base);
    const unwrapped = unwrapProxy(abs, origin);
    if (!unwrapped) return null;
    if (unwrapped.protocol !== "http:" && unwrapped.protocol !== "https:") return null;
    unwrapped.hash = "";
    return unwrapped.href;
  } catch {
    return null;
  }
}

function unwrapProxy(url: URL, origin: string): URL | null {
  if (url.origin !== origin) return url;
  const match = url.pathname.match(/^\/p\/(https?)\/([^/]+)(\/.*)?$/i);
  if (!match) return url;
  try {
    return new URL(`${match[1]}://${match[2]}${match[3] || "/"}${url.search}`);
  } catch {
    return null;
  }
}

function box(
  x: number, y: number, w: number, h: number, bg: number | null, grads: Grad[], images: RawImage[],
  border: BoxPaint["border"], shadows: Shadow[], opacity: number, clip: Rect | null, radius: number[],
): BoxPaint {
  return { k: "box", x, y, w, h, radius, bg, grads, images, border, shadows, opacity, clip };
}

export function viewportWidth(doc: Document): number {
  const content = doc.querySelector('meta[name="viewport" i]')?.getAttribute("content") ?? "";
  const m = content.match(/(?:^|[,;\s])width\s*=\s*(\d+)/i);
  return m ? Number(m[1]) : 0;
}
